/**
 * @lines98/client/lib/gnome-card-fit — общее решение «карточка гнома → крупный (single) режим».
 *
 * ЗАЧЕМ / WHY THIS EXISTS (owner-directive 2026-06-15 «одна в ряд → растёт» + owner-playtest
 * 2026-06-20 «карточка-награда того же размера, что на Помощники»). Карточка гнома показывается
 * в ДВУХ местах: сетка экрана «Помощники» (`gnomes-screen` / `.gnomes-list`) и модальный оверлей
 * открытия помощника (`lib/helper-unlock-overlay`). В ОБОИХ «тот же размер» означает: когда по
 * ширине помещается <2 КОМФОРТНЫХ карточек (узкий телефон) — карточка растёт в крупный
 * пропорциональный режим (CSS `--single`); при ≥2 — обычный размер сетки. Чтобы оверлей-карточка
 * совпадала с сеткой на ВСЕХ вьюпортах (вкл. телефон), оба места считают режим ОДНОЙ формулой
 * (модель «ствол → ветки», §1: общее поведение = модуль, не копия по экранам).
 *
 * SCOPE: чистая функция решения (юнит-тестируемо без DOM) + порог-константа. NOT IN SCOPE:
 * измерение DOM (делают потребители — у каждого свой avail/card-элемент), сам CSS `--single`
 * (styles/gnomes.css — единый блок-sizing для `.gnomes-list--single` И `.helper-unlock-overlay
 * --single`).
 *
 * Cross-references:
 *   - screens/gnomes-screen.ts (потребитель: ResizeObserver на `.gnomes-list`).
 *   - lib/helper-unlock-overlay.ts (потребитель: ResizeObserver на dialog-оверлее).
 *   - styles/gnomes.css (`.gnomes-list--single .gnome-card`, `.helper-unlock-overlay--single
 *     .gnome-card` — единый sizing-блок, применяется по этому решению).
 */

/**
 * Комфортная ширина карточки (тактильный floor читаемой карточки, px; owner-directive
 * 2026-06-15 «подними порог»). Поднимает порог single-режима: на телефонах (натуральная
 * ширина < floor) две мелкие не показываем — одна крупная пропорциональная. tunable.
 */
export const GNOME_CARD_COMFORT_MIN = 260;

/**
 * Включать ли крупный (single) режим: в доступную ширину помещается <2 КОМФОРТНЫХ карточек.
 * Чистая функция (потребитель сам измеряет DOM и передаёт px). Защитна к нулям/мусору
 * (≤0 → false: не знаем размеры → не трогаем).
 *
 * @param availWidth — доступная ширина контейнера, px (listEl.clientWidth / dialog.clientWidth).
 * @param cardWidth — ФАКТИЧЕСКАЯ натуральная ширина карточки в обычном режиме, px (замер со
 *   снятым `--single`: формула недооценивает на мелком размере из-за floor'ов иконок/шрифтов).
 * @param gap — зазор между карточками, px (= `--m`).
 * @param comfortMin — комфорт-floor ширины (по умолчанию `GNOME_CARD_COMFORT_MIN`).
 */
export function gnomeCardSingleMode(
  availWidth: number,
  cardWidth: number,
  gap: number,
  comfortMin: number = GNOME_CARD_COMFORT_MIN,
): boolean {
  if (cardWidth <= 0 || availWidth <= 0) return false;
  const comfortW = Math.max(cardWidth, comfortMin);
  const fits = Math.floor((availWidth + gap) / (comfortW + gap));
  return fits < 2;
}

/**
 * Делитель ширины контейнера для cell-size крупного (single) режима. ДОЛЖЕН совпадать с
 * CSS-формулой `--cell-size: round(down, calc(100cqw / 4.0), 1px)` в styles/gnomes.css
 * (`.gnomes-list--single .gnome-card`) — CSS остаётся source-of-truth рендера; JS лишь
 * ПРЕ-резолвит её результат в конкретный px ради пина (zero-CLS) + height-fit (getComputedStyle
 * НЕ резолвит `round()/cqw` для нерегистрированных custom-property → пин строки-выражения был бы
 * не-числом). `1cqw` = 1% ширины контейнера `.gnome-card` (он несёт `container-type:inline-size`
 * в single) → widthCell = floor(cardWidth / 4). Меняешь тут — меняй и CSS-формулу.
 */
export const SINGLE_CELL_CQW_DIVISOR = 4.0;

/**
 * Нижний floor cell-size крупного (single) режима, px. Трассировка: `--fs-control = max(13px,
 * cell·0.3)` (global.css) → управляющий текст перестаёт расти ниже cell = 13/0.3 ≈ 43.3px =
 * floor ЧИТАЕМОСТИ. Ниже этого cell дальше ужимать нет смысла — текст/иконки/кнопка уже на
 * СВОИХ CSS-floor'ах (текст 13px, кнопка `--btn-h` 44px, иконки 20px) и НЕ уменьшаются → P2
 * тактильность кнопки СОХРАНЕНА её собственным `max(44px, …)` даже при cell ниже 62.86 (поэтому
 * floor здесь = читаемость 43.3, НЕ кнопочные 62.86: button-floor избыточен — кнопка защищена
 * сама). При cell-floor’е cell-производные ЗАЗОРЫ/высота-героя ещё ужимаются → карточка влезает
 * заметно ниже. P1 visibility (§0.A) приоритетнее P3 no-scroll: на крошечном экране карточка
 * вписывается за счёт cell-spacing, кнопка остаётся тактильной. Остаток (если экран совсем
 * мал) — bounded inner-scroll `.gnomes-scroll` (§1.2), НЕ молчаливая обрезка. tunable.
 */
export const GNOME_SINGLE_CELL_FLOOR = 13 / 0.3;

/**
 * Высото-bounded cell для крупного (single) карточки-гнома. Применяет board-дисциплину
 * `cell = min(width-bound, height-bound)` (responsive-ui §3 / viewport-sizer): ширино-производный
 * cell (cqw, owner-directive 2026-06-15 «одна в ряд → растёт») на НИЗКОМ портрете (320×480 /
 * 375×667) делает карточку выше viewport → bonus-pill + низ карточки уезжают за кадр (P1-нарушение
 * §0.A, owner visual-прогон). Лечим как доска: дополнительно ограничиваем cell высотой.
 *
 * Все размеры карточки выводятся ЛИНЕЙНО от `--cell-size` (gnome-card.css / trophy-card.css) →
 * высота карточки ∝ cell. Поэтому height-fit cell = widthCell · (availHeight / cardHeight)
 * (алгебраически точно, БЕЗ угаданных коэффициентов §0.G). Возвращаем `min(widthCell,
 * heightCell)`, но не ниже floor читаемости/тактильности (§0.A P2 > P3). Это НЕ верхний кап
 * (§0.G.5): height-bound viewport-относителен и масштабируется с экраном (как у доски).
 *
 * Защитна к нулям/мусору: некорректные замеры → возвращаем widthCell без изменений.
 *
 * @param widthCell — ширино-производный cell (resolved cqw px из CSS-формулы), px.
 * @param cardHeight — фактическая высота карточки при widthCell, px.
 * @param availHeight — доступная высота под карточку (scroll-регион), px.
 * @param floor — нижний floor cell (по умолчанию `GNOME_SINGLE_CELL_FLOOR`).
 */
export function gnomeSingleCellHeightFit(
  widthCell: number,
  cardHeight: number,
  availHeight: number,
  floor: number = GNOME_SINGLE_CELL_FLOOR,
): number {
  if (widthCell <= 0 || cardHeight <= 0 || availHeight <= 0) return widthCell;
  if (cardHeight <= availHeight) return widthCell; // уже влезает — не трогаем
  const heightCell = widthCell * (availHeight / cardHeight);
  return Math.max(floor, Math.min(widthCell, heightCell));
}
